// Canonical end-to-end flow:
//
//   1. Confirm context  (organization_id + workspace_id picked in the topbar)
//   2. Create project    POST /media/projects
//   3. Add document      POST /media/projects/{id}/documents/{filename}
//   4. Upload bytes      PUT  {target_signed_url}
//   5. Generate media    POST /media/projects/{id}/media/generate
//
// Mirrors the "Quick Start" sequence in the Libertify API doc (Notion).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { IdChip } from '@/components/IdChip';
import type { ApiResponseSnapshot, RequestSpec } from '@/types';
import {
  Field,
  FormCard,
  Select,
  TextArea,
  TextInput,
  TagsInput,
} from '@/playgrounds/_shared';
import { TONE_IDS, type ToneId, DOCUMENT_TYPES, type DocumentType } from '@/playgrounds/_constants';
import { FlowProvider, useFlow } from './_state';
import { FlowHeader, StepBody, StepCard } from './_components';
import type { FlowContext } from './types';

const STEPS: { id: string; label: string; hint: string }[] = [
  { id: 'context',  label: 'Context',         hint: 'Pick organization + workspace' },
  { id: 'project',  label: 'Create project',  hint: 'POST /media/projects' },
  { id: 'document', label: 'Add document',    hint: 'POST /media/projects/{id}/documents/{filename}' },
  { id: 'upload',   label: 'Upload file',     hint: 'PUT to the presigned URL' },
  { id: 'generate', label: 'Generate media',  hint: 'POST /media/projects/{id}/media/generate' },
];

export function CreateAndPublishFlow() {
  const { organizationId, workspaceId } = useAuth();
  return (
    <FlowProvider
      flowId="create-and-publish"
      initialActiveStepId="context"
      initialContext={{
        organization_id: organizationId || undefined,
        workspace_id: workspaceId || undefined,
      }}
    >
      <FlowBody />
    </FlowProvider>
  );
}

function FlowBody() {
  const flow = useFlow();
  const { organizationId, workspaceId } = useAuth();

  // Keep flow.context.organization_id / workspace_id in sync with the
  // topbar pickers (the user can change them mid-flow).
  useEffect(() => {
    flow.setContext({
      organization_id: organizationId || undefined,
      workspace_id: workspaceId || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, workspaceId]);

  return (
    <div className="lp-space-y-5">
      <FlowHeader
        eyebrow="Quickstart flow"
        title="Create a project, upload a document, generate media"
        description="The canonical end-to-end pipeline. Each step pre-fills its inputs from the previous step's response — what you see is exactly the request your code will send."
        steps={STEPS}
      />

      <div className="lp-space-y-4">
        <ContextStep />
        <CreateProjectStep />
        <AddDocumentStep />
        <UploadFileStep />
        <GenerateMediaStep />
      </div>

      <CompletionBanner />
    </div>
  );
}

// =============================================================================
// Step 1 — Context
// =============================================================================

function ContextStep() {
  const flow = useFlow();
  const { organizations, workspaces, organizationId, workspaceId, discoveringContext, contextError, reloadContext } = useAuth();

  return (
    <StepCard
      stepId="context"
      index={0}
      total={STEPS.length}
      title="Confirm your context"
      subtitle="organization_id + workspace_id"
      description="The Libertify Auth API returns the organizations and workspaces you have access to. Pick the destination for the project you're about to create."
    >
      {discoveringContext && (
        <div className="lp-text-[12.5px] lp-flex lp-items-center lp-gap-2" style={{ color: '#64748b' }}>
          <Spinner /> Loading your organizations…
        </div>
      )}

      {!discoveringContext && contextError && (
        <div
          className="lp-text-[12.5px] lp-px-3 lp-py-2.5 lp-flex lp-items-center lp-justify-between lp-gap-3"
          style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', borderRadius: 10 }}
        >
          <span>Couldn&apos;t load your organizations: {contextError}</span>
          <button
            onClick={reloadContext}
            className="lp-text-[11.5px] lp-font-semibold lp-px-2.5 lp-py-1 lp-rounded-md"
            style={{ background: '#ffffff', border: '1px solid #fecdd3', color: '#9f1239' }}
          >
            Retry
          </button>
        </div>
      )}

      <div className="lp-grid lp-gap-3">
        <Field
          label="Organization"
          hint={`${organizations.length} available — change in the top bar to switch contexts`}
        >
          <code
            className="lp-block lp-px-3 lp-py-2 lp-text-[12.5px] lp-font-mono lp-rounded-xl"
            style={{ background: '#f8f7ff', border: '1px solid #cbc4ff', color: '#3328aa' }}
          >
            {organizationId || '— pick one in the top bar —'}
          </code>
        </Field>
        <Field
          label="Workspace"
          hint={
            workspaces.length === 0
              ? 'No workspaces fetched yet — defaults will be used'
              : `${workspaces.length} available — change in the top bar to switch`
          }
        >
          <code
            className="lp-block lp-px-3 lp-py-2 lp-text-[12.5px] lp-font-mono lp-rounded-xl"
            style={{ background: '#f8f7ff', border: '1px solid #cbc4ff', color: '#3328aa' }}
          >
            {workspaceId || '— defaults to your primary workspace —'}
          </code>
        </Field>
      </div>

      <div className="lp-flex lp-items-center lp-justify-end lp-pt-2">
        <button
          disabled={!organizationId}
          onClick={() => {
            flow.setContext({ organization_id: organizationId, workspace_id: workspaceId || undefined });
            flow.recordStepResult('context', {
              produced: { organization_id: organizationId, workspace_id: workspaceId || undefined },
              response: {
                status: 200,
                ok: true,
                durationMs: 0,
                body: { organization_id: organizationId, workspace_id: workspaceId || null },
                headers: {},
              },
            });
            flow.setActiveStepId('project');
          }}
          className="lp-btn-primary lp-px-5 lp-py-2 lp-text-sm lp-font-bold"
          style={{ borderRadius: 12 }}
        >
          Use this context →
        </button>
      </div>
    </StepCard>
  );
}

function Spinner() {
  return (
    <svg className="lp-w-3.5 lp-h-3.5 lp-animate-spin" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="lp-opacity-25" />
      <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// =============================================================================
// Step 2 — Create Project
// =============================================================================

function CreateProjectStep() {
  const flow = useFlow();

  const [title, setTitle] = useState('My first API project');
  const [type, setType] = useState('flash');
  const [description, setDescription] = useState('Created from the Libertify API playground');
  const [toneId, setToneId] = useState<ToneId | ''>('');
  const [tags, setTags] = useState<{ tag_key: string; tag_value: string }[]>([
    { tag_key: 'playground', tag_value: 'playground' },
  ]);

  const spec: RequestSpec | null = useMemo(() => {
    const orgId = flow.context.organization_id;
    if (!orgId) return null;
    const body: Record<string, unknown> = {
      title,
      type,
      organization_id: orgId,
    };
    if (flow.context.workspace_id) body.workspace_id = flow.context.workspace_id;
    if (description) body.description = description;
    if (toneId) body.tone_id = toneId;
    if (tags.length) body.tags = tags;
    return { method: 'POST', path: '/media/projects', body };
  }, [flow.context.organization_id, flow.context.workspace_id, title, type, description, toneId, tags]);

  const handleSuccess = (resp: ApiResponseSnapshot): Partial<FlowContext> | undefined => {
    const body = resp.body as Record<string, unknown> | null;
    const id = typeof body?.id === 'string' ? body.id : undefined;
    if (!id) return undefined;
    flow.setActiveStepId('document');
    return { project_id: id, project_title: title, project_type: type };
  };

  return (
    <StepCard
      stepId="project"
      index={1}
      total={STEPS.length}
      title="Create the project"
      subtitle="POST /media/projects"
      description="A project is the top-level container for everything else (documents, storyboards, media). The Libertify web app uses type=flash for the standard interactive-experience flow."
      requires={['organization_id']}
    >
      <StepBody stepId="project" spec={spec} onRunSuccess={handleSuccess} runLabel="Create project">
        <FormCard>
          <Field label="Title" hint="Required — displayed everywhere the project shows up">
            <TextInput value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
          <Field label="Type" hint='Use "flash" for the standard interactive experience'>
            <Select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="flash">flash (recommended)</option>
              <option value="Video">Video</option>
              <option value="Document">Document</option>
              <option value="Presentation">Presentation</option>
              <option value="Podcast">Podcast</option>
            </Select>
          </Field>
          <Field label="Description">
            <TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field label="Tone of voice">
            <Select value={toneId} onChange={(e) => setToneId(e.target.value as ToneId | '')}>
              <option value="">— Default —</option>
              {TONE_IDS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tags" hint="Each label is sent as {tag_key, tag_value} with both fields equal">
            <TagsInput value={tags} onChange={setTags} />
          </Field>
        </FormCard>
      </StepBody>

      {flow.context.project_id && (
        <div className="lp-mt-4 lp-flex lp-items-center lp-gap-2 lp-flex-wrap">
          <IdChip label="project_id" value={flow.context.project_id} />
          <button
            onClick={() => flow.setActiveStepId('document')}
            className="lp-text-[11.5px] lp-font-semibold"
            style={{ color: '#6256ff' }}
          >
            Continue to upload →
          </button>
        </div>
      )}
    </StepCard>
  );
}

// =============================================================================
// Step 3 — Add Document (creates the entry, returns the signed URL)
// =============================================================================

function AddDocumentStep() {
  const flow = useFlow();

  const [filename, setFilename] = useState('mydoc.pdf');
  const [name, setName] = useState('My first uploaded document');
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState<DocumentType>('Unknown');
  const [pickedFile, setPickedFile] = useState<File | null>(null);

  // Auto-sync filename whenever the user picks a file.
  useEffect(() => {
    if (pickedFile) {
      setFilename(pickedFile.name);
      if (!name || name === 'My first uploaded document') {
        setName(pickedFile.name.replace(/\.[a-z0-9]+$/i, ''));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedFile]);

  const projectId = flow.context.project_id;
  const spec: RequestSpec | null = useMemo(() => {
    if (!projectId || !filename) return null;
    const body: Record<string, unknown> = { name, document_type: documentType };
    if (description) body.description = description;
    return {
      method: 'POST',
      path: `/media/projects/${projectId}/documents/${encodeURIComponent(filename)}`,
      body,
    };
  }, [projectId, filename, name, description, documentType]);

  const handleSuccess = (resp: ApiResponseSnapshot): Partial<FlowContext> | undefined => {
    const body = resp.body as Record<string, unknown> | null;
    const id =
      (typeof body?.id === 'string' && body.id) ||
      (typeof body?.document_id === 'string' && body.document_id) ||
      undefined;
    const target =
      (typeof body?.target === 'string' && body.target) ||
      (typeof body?.target_signed_url === 'string' && body.target_signed_url) ||
      undefined;
    if (!id || !target) return undefined;
    flow.setActiveStepId('upload');
    return {
      document_id: id,
      document_filename: filename,
      document_signed_url: target,
      document_uploaded: false,
    };
  };

  // The file the user picked must be stashed in flow context so step 4 can
  // PUT it. We can't put a Blob in JSON-serializable state, so use a ref.
  const fileRef = (window as unknown as { __libertifyFlowFile?: File | null });
  fileRef.__libertifyFlowFile = pickedFile;

  return (
    <StepCard
      stepId="document"
      index={2}
      total={STEPS.length}
      title="Reserve a slot for your document"
      subtitle="POST /media/projects/{id}/documents/{filename}"
      description="This call doesn't upload bytes yet — it returns a presigned URL ('target') that you'll PUT the file to in the next step. Pick the file now so we can keep it warm in your browser."
      requires={['project_id']}
    >
      <FilePicker file={pickedFile} onPick={setPickedFile} />

      <StepBody stepId="document" spec={spec} onRunSuccess={handleSuccess} runLabel="Get signed URL">
        <FormCard>
          <Field label="Filename" hint="Used in the URL path — must match the actual file extension">
            <TextInput value={filename} onChange={(e) => setFilename(e.target.value)} required />
          </Field>
          <Field label="Display name" hint="Shown in the Libertify UI">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <Field label="Document type" hint="Use 'Unknown' if your content doesn't fit a specific category">
            <Select value={documentType} onChange={(e) => setDocumentType(e.target.value as DocumentType)}>
              {DOCUMENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Description (optional)">
            <TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </FormCard>
      </StepBody>

      {flow.context.document_signed_url && (
        <div className="lp-mt-4 lp-flex lp-items-center lp-gap-2 lp-flex-wrap">
          <IdChip label="document_id" value={flow.context.document_id || ''} />
          <span
            className="lp-text-[10.5px] lp-font-bold lp-uppercase lp-px-2 lp-py-1 lp-rounded"
            style={{ background: '#ecfdf5', color: '#047857', letterSpacing: '0.06em' }}
          >
            Signed URL ready
          </span>
        </div>
      )}
    </StepCard>
  );
}

function FilePicker({ file, onPick }: { file: File | null; onPick: (f: File | null) => void }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dragRef = useRef<HTMLDivElement | null>(null);
  const [drag, setDrag] = useState(false);

  return (
    <div
      ref={dragRef}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onPick(f);
      }}
      onClick={() => inputRef.current?.click()}
      className="lp-flex lp-items-center lp-gap-4 lp-px-4 lp-py-4 lp-cursor-pointer lp-mb-3"
      style={{
        background: drag ? '#f1f0ff' : file ? '#f0fdf4' : '#f8fafc',
        border: `2px dashed ${drag ? '#6256ff' : file ? '#86efac' : '#cbd5e1'}`,
        borderRadius: 16,
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        hidden
        accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.txt,.html,.png,.jpg,.jpeg,.gif"
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />
      <div
        className="lp-shrink-0 lp-flex lp-items-center lp-justify-center"
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: file ? '#10b981' : 'linear-gradient(135deg, #f1f0ff, #e4e0ff)',
          color: file ? '#ffffff' : '#4f3ff3',
        }}
      >
        {file ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 0 1-.88-7.9A5 5 0 0 1 15.9 6 4.5 4.5 0 1 1 18 15M12 12v8m0-8l-3 3m3-3l3 3" />
          </svg>
        )}
      </div>
      <div className="lp-flex-1 lp-min-w-0">
        {file ? (
          <>
            <div className="lp-text-sm lp-font-semibold lp-truncate" style={{ color: '#0f172a' }}>
              {file.name}
            </div>
            <div className="lp-text-[12px]" style={{ color: '#475569' }}>
              {formatBytes(file.size)} · ready to upload
            </div>
          </>
        ) : (
          <>
            <div className="lp-text-sm lp-font-semibold" style={{ color: '#0f172a' }}>
              Drop a file here, or click to browse
            </div>
            <div className="lp-text-[12px]" style={{ color: '#64748b' }}>
              PDF, DOCX, XLSX, PPTX, TXT, HTML, images — up to 100 MB
            </div>
          </>
        )}
      </div>
      {file && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPick(null);
          }}
          className="lp-text-[11px] lp-font-medium lp-px-2 lp-py-1 lp-rounded-md lp-shrink-0"
          style={{ color: '#64748b', background: '#ffffff', border: '1px solid #e5e7eb' }}
        >
          Remove
        </button>
      )}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// =============================================================================
// Step 4 — Upload bytes (PUT to the signed URL)
// =============================================================================

function UploadFileStep() {
  const flow = useFlow();
  const [progress, setProgress] = useState<number>(0);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<ApiResponseSnapshot | null>(null);

  const target = flow.context.document_signed_url;
  const filename = flow.context.document_filename;
  const ready = !!target && !!filename;

  const upload = async () => {
    if (!target) return;
    setError(null);
    setProgress(0);
    setRunning(true);
    try {
      const file = (window as unknown as { __libertifyFlowFile?: File | null }).__libertifyFlowFile;
      if (!file) throw new Error('Pick a file in step 3 first.');
      const start = performance.now();
      const r = await putWithProgress(target, file, setProgress);
      const snap: ApiResponseSnapshot = {
        status: r.status,
        ok: r.status >= 200 && r.status < 300,
        durationMs: performance.now() - start,
        body: r.body || '(no body)',
        headers: r.headers,
      };
      setResponse(snap);
      if (snap.ok) {
        flow.recordStepResult('upload', {
          response: snap,
          produced: { document_uploaded: true },
        });
        flow.setActiveStepId('generate');
      } else {
        flow.recordStepResult('upload', { response: snap });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  // Generated curl/JS-fetch snippet for the PUT — special-cased because the
  // body is binary, not JSON.
  const snippets = ready ? buildUploadSnippets(target!, filename!) : null;

  return (
    <StepCard
      stepId="upload"
      index={3}
      total={STEPS.length}
      title="Upload the file to S3"
      subtitle="PUT {target_signed_url}"
      description="The presigned URL accepts a single PUT with the file bytes. Critical: do NOT include the Authorization header here — the URL is already signed by S3, and adding a Bearer token will make S3 reject the request."
      requires={['document_signed_url']}
    >
      <div className="lp-grid lp-grid-cols-1 xl:lp-grid-cols-[minmax(0,1.35fr)_minmax(360px,440px)] lp-gap-6 lp-mt-3">
        <div className="lp-space-y-4">
          <FormCard>
            <Field label="Signed URL" hint="Returned by step 3 — expires in 60 minutes">
              <code
                className="lp-block lp-px-3 lp-py-2 lp-text-[11px] lp-font-mono lp-rounded-xl lp-overflow-x-auto lp-whitespace-nowrap"
                style={{ background: '#0f172a', color: '#a99dff' }}
              >
                {target || '— no signed URL yet —'}
              </code>
            </Field>
            <Field label="File">
              <code
                className="lp-block lp-px-3 lp-py-2 lp-text-[12px] lp-font-mono lp-rounded-xl"
                style={{ background: '#f8f7ff', border: '1px solid #cbc4ff', color: '#3328aa' }}
              >
                {filename || '—'}
              </code>
            </Field>
          </FormCard>

          {running && (
            <div
              className="lp-px-4 lp-py-3"
              style={{ background: '#f8f7ff', border: '1px solid #cbc4ff', borderRadius: 14 }}
            >
              <div className="lp-flex lp-items-center lp-justify-between lp-mb-2">
                <span className="lp-text-[12px] lp-font-semibold" style={{ color: '#3328aa' }}>
                  Uploading…
                </span>
                <span className="lp-text-[12px] lp-tabular-nums" style={{ color: '#475569' }}>
                  {Math.round(progress * 100)}%
                </span>
              </div>
              <div style={{ height: 6, background: '#e0d9ff', borderRadius: 9999, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${Math.round(progress * 100)}%`,
                    background: 'linear-gradient(135deg, #6256ff, #7c3aed)',
                    transition: 'width 80ms linear',
                  }}
                />
              </div>
            </div>
          )}

          <button
            onClick={upload}
            disabled={!ready || running}
            className="lp-btn-primary lp-w-full lp-py-2.5 lp-text-sm lp-font-bold"
            style={{ borderRadius: 12 }}
          >
            {running ? 'Uploading…' : 'Upload file'}
          </button>
        </div>

        <div className="lp-space-y-4 lp-self-start">
          {snippets && <UploadCodeTabs snippets={snippets} />}
          {error && (
            <div
              className="lp-rounded-2xl lp-p-4 lp-text-sm lp-shadow-card"
              style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', borderRadius: 22 }}
            >
              <div className="lp-font-semibold lp-mb-1">Upload failed</div>
              <div className="lp-font-mono lp-text-xs lp-whitespace-pre-wrap">{error}</div>
            </div>
          )}
          {response && (
            <div
              className="lp-overflow-hidden lp-shadow-card lp-surface-dark"
              style={{ borderRadius: 22 }}
            >
              <div
                className="lp-flex lp-items-center lp-gap-3 lp-px-4 lp-py-2.5"
                style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
              >
                <span
                  className="lp-text-[10px] lp-font-bold lp-px-2 lp-py-0.5 lp-rounded lp-tracking-wider"
                  style={{
                    background: response.ok ? '#a7f3d0' : '#fda4af',
                    color: response.ok ? '#065f46' : '#9f1239',
                  }}
                >
                  {response.status} {response.ok ? 'OK' : 'ERR'}
                </span>
                <span className="lp-text-[11px]" style={{ color: '#cbd5e1' }}>
                  {response.durationMs.toFixed(0)} ms
                </span>
              </div>
              <pre className="lp-code-pane lp-text-[12px] lp-font-mono lp-p-4 lp-max-h-[200px] lp-overflow-auto" style={{ color: '#e2e8f0' }}>
                {typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </StepCard>
  );
}

// XHR-based PUT so we can wire upload progress events.
function putWithProgress(
  url: string,
  body: Blob,
  onProgress: (fraction: number) => void
): Promise<{ status: number; body: string; headers: Record<string, string> }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    // Notion explicitly says: send Content-Type as empty string. Browsers
    // won't let us set it to literally empty, but they also won't add one
    // to a Blob PUT unless we ask — so we just don't set it.
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      const headers: Record<string, string> = {};
      xhr
        .getAllResponseHeaders()
        .split(/\r?\n/)
        .forEach((line) => {
          const idx = line.indexOf(':');
          if (idx > 0) headers[line.slice(0, idx).trim().toLowerCase()] = line.slice(idx + 1).trim();
        });
      resolve({ status: xhr.status, body: xhr.responseText, headers });
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.send(body);
  });
}

interface UploadSnippets {
  curl: string;
  js: string;
  python: string;
}

function buildUploadSnippets(target: string, filename: string): UploadSnippets {
  const curl = `curl -X PUT '${target}' \\
  --upload-file './${filename}'`;

  const js = `// 'file' is a File or Blob from an <input type="file"> or fetch().
const r = await fetch('${target}', {
  method: 'PUT',
  body: file,
  // IMPORTANT: do NOT set an Authorization header — the URL is already
  // signed by S3, and a Bearer token will make S3 reject the request.
});
console.log(r.status, r.statusText);`;

  const python = `import requests

with open('${filename}', 'rb') as f:
    r = requests.put(
        '${target}',
        data=f,
        # No Authorization header — the URL is already signed by S3.
    )
print(r.status_code)`;

  return { curl, js, python };
}

function UploadCodeTabs({ snippets }: { snippets: UploadSnippets }) {
  const [active, setActive] = useState<'curl' | 'js' | 'python'>('curl');
  const [copied, setCopied] = useState(false);
  const code = snippets[active];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1300);
    } catch {
      /* ignore */
    }
  };

  const tabs: { id: typeof active; label: string }[] = [
    { id: 'curl', label: 'cURL' },
    { id: 'js', label: 'JavaScript' },
    { id: 'python', label: 'Python' },
  ];

  return (
    <div className="lp-surface-dark lp-overflow-hidden lp-shadow-card" style={{ borderRadius: 22 }}>
      <div
        className="lp-flex lp-overflow-x-auto lp-items-center lp-gap-1 lp-px-2 lp-py-1.5"
        role="tablist"
        aria-label="Code language"
        style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        {tabs.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              role="tab"
              aria-selected={isActive}
              className="lp-relative lp-whitespace-nowrap lp-rounded-lg lp-transition-colors lp-shrink-0"
              style={{
                padding: '7px 12px',
                fontSize: 13,
                lineHeight: '18px',
                fontWeight: isActive ? 600 : 500,
                letterSpacing: 0.1,
                color: isActive ? '#ffffff' : '#cbd5e1',
                background: isActive ? 'rgba(124, 58, 237, 0.22)' : 'transparent',
                border: isActive
                  ? '1px solid rgba(167, 139, 250, 0.45)'
                  : '1px solid transparent',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.color = '#cbd5e1';
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div className="lp-relative">
        <pre
          className="lp-code-pane lp-text-[12.5px] lp-font-mono lp-p-4 lp-pr-20 lp-overflow-auto lp-max-h-[280px]"
          style={{ color: '#e2e8f0' }}
        >
          {code}
        </pre>
        <button
          onClick={copy}
          aria-label="Copy code"
          className="lp-absolute lp-top-2.5 lp-right-2.5 lp-text-[11px] lp-font-medium lp-px-2.5 lp-py-1 lp-rounded-md lp-transition-colors"
          style={{
            background: copied ? 'rgba(124, 58, 237, 0.32)' : 'rgba(255,255,255,0.08)',
            color: '#e2e8f0',
            backdropFilter: 'blur(6px)',
          }}
          onMouseEnter={(e) => {
            if (!copied) e.currentTarget.style.background = 'rgba(255,255,255,0.16)';
          }}
          onMouseLeave={(e) => {
            if (!copied) e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Step 5 — Generate Media
// =============================================================================

function GenerateMediaStep() {
  const flow = useFlow();
  const projectId = flow.context.project_id;

  const spec: RequestSpec | null = useMemo(() => {
    if (!projectId) return null;
    return { method: 'POST', path: `/media/projects/${projectId}/media/generate` };
  }, [projectId]);

  return (
    <StepCard
      stepId="generate"
      index={4}
      total={STEPS.length}
      title="Generate the experience"
      subtitle="POST /media/projects/{id}/media/generate"
      description="Kick off the storyboard → slides → media pipeline. Once submitted, listen for `MediaStatusChanged` events on the WebSocket (or poll GET /media/{media_id}) to know when it's ready."
      requires={['project_id', 'document_uploaded']}
    >
      <StepBody
        stepId="generate"
        spec={spec}
        runLabel="Generate media"
        onRunSuccess={(resp) => {
          const body = resp.body as Record<string, unknown> | null;
          const mediaId =
            (typeof body?.media_id === 'string' && body.media_id) ||
            (typeof body?.id === 'string' && body.id) ||
            undefined;
          return mediaId ? { media_id: mediaId } : undefined;
        }}
      >
        <div
          className="lp-px-4 lp-py-3 lp-text-sm"
          style={{ background: '#f0f9ff', border: '1px solid #bae6fd', color: '#075985', borderRadius: 12 }}
        >
          <div className="lp-font-semibold lp-mb-1">No body required</div>
          <p className="lp-text-[12.5px]" style={{ color: '#0c4a6e' }}>
            The server uses the project&apos;s saved settings (or org defaults) to drive generation.
            Override behaviour by passing a <code style={inlineCode}>saved_settings_id</code> when you create the project,
            or by attaching a Brand kit / Playbook from the related playgrounds.
          </p>
        </div>
      </StepBody>
    </StepCard>
  );
}

const inlineCode: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  background: '#ffffff',
  padding: '1px 5px',
  borderRadius: 4,
  fontSize: 11.5,
};

// =============================================================================
// CompletionBanner — congrats + next steps
// =============================================================================

function CompletionBanner() {
  const flow = useFlow();
  const allDone = ['context', 'project', 'document', 'upload', 'generate'].every(
    (s) => flow.results[s]?.ranAt && (flow.results[s]?.response?.ok ?? true)
  );
  if (!allDone) return null;
  const projectUrl = flow.context.project_id
    ? `https://app.libertify.com/media/projects/${flow.context.project_id}`
    : null;
  return (
    <div
      className="lp-px-6 lp-py-5 lp-shadow-card-lg"
      style={{
        background:
          'radial-gradient(circle at top right, rgba(98,86,255,0.18), transparent 45%), linear-gradient(135deg, #050816 0%, #0b1028 55%, #11104a 100%)',
        color: '#ffffff',
        borderRadius: 22,
      }}
    >
      <div className="lp-flex lp-items-start lp-gap-3 lp-flex-wrap">
        <span
          className="lp-text-2xl"
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'linear-gradient(135deg, #6256ff, #7c3aed)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
          }}
        >
          ✓
        </span>
        <div className="lp-flex-1 lp-min-w-0">
          <h3 className="lp-text-lg lp-font-bold">Your project is in the oven</h3>
          <p className="lp-text-sm lp-mt-1" style={{ color: '#cbd5e1' }}>
            Generation typically takes 30 seconds to a few minutes depending on the document size. Open the
            project in the Libertify app to watch the live progress, or subscribe to the WebSocket from your
            backend to get notified when <code style={{ background: 'rgba(255,255,255,0.12)', padding: '1px 5px', borderRadius: 4 }}>MediaStatusChanged</code>
            {' '}flips to{' '}
            <code style={{ background: 'rgba(255,255,255,0.12)', padding: '1px 5px', borderRadius: 4 }}>Ready</code>.
          </p>
          {projectUrl && (
            <a
              href={projectUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="lp-inline-flex lp-items-center lp-gap-1.5 lp-mt-3 lp-text-sm lp-font-bold"
              style={{
                background: '#ffffff',
                color: '#3328aa',
                padding: '8px 14px',
                borderRadius: 9999,
              }}
            >
              Open in Libertify app ↗
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
